import { isUserMessage } from '../lib/utils';
import { PROB_QUOTE_PREFIX } from '../constants';

export function buildMemoSuggestionText(messages, latestText, messageCount) {
  const count = messageCount || (messages.filter(isUserMessage).length + 1);
  return `Sinkに ${count} 通の投稿があります。そろそろNoteまとめをして新しいチャットに切り替えませんか？`;
}

export function buildAssetSelectionPrompt({ mode, history, text, thinkContext, concepts, notes, insights }) {
  const recentHistory = history
    .slice(-12)
    .map((message) => `${(message.source ?? message.role) === 'assistant' ? 'AI' : 'User'}: ${message.body ?? message.text}`)
    .join('\n\n');

  return `入力モード: ${mode}

現在のThinkコンテキスト (ユーザーが現在注目しているマップ上の位置):
${thinkContext || 'ルート'}

直近の会話履歴:
${recentHistory || 'なし'}

今回のユーザー入力:
${text}

利用可能なAsset一覧:
${concepts}

利用可能なNote一覧:
${notes}

利用可能なHint一覧:
${insights}

上記を踏まえて、今回のユーザーの思考を深めるために参照すべき関連ナレッジを選び、そのIDをJSON形式で出力してください。
- Asset: 最大3件
- Note: 最大2件
- Hint: 最大2件

関連するものがなければ空の配列を返してください。
余計なテキストは含めず、必ず以下の形式のJSONのみを出力してください。

\`\`\`json
{
  "referencedAssetIds": ["asset_id1"],
  "referencedNoteIds": ["note_id1"],
  "referencedInsightIds": ["insight_id1"]
}
\`\`\``;
}

export function buildThinkingPrompt({ mode, history, text, selectedAssetsText, thinkContext, isDeepDiscuss }) {
  const recentHistory = history
    .slice(-12)
    .map((message) => `${(message.source ?? message.role) === 'assistant' ? 'AI' : 'User'}: ${message.body ?? message.text}`)
    .join('\n\n');

  return `入力モード: ${mode}

参照文脈 (Asset / Note / Hint / Shared Asset):
${selectedAssetsText || 'なし'}

現在のThinkコンテキスト (現在閲覧中の思考マップ):
${thinkContext || 'ルート'}

直近の会話履歴:
${recentHistory || 'なし'}

今回のユーザー入力:
${text}

文脈、参照Asset、および現在のThinkコンテキストを踏まえて、ユーザーの思考を受け止め、次の一歩につながる問いかけや整理を日本語で返してください。${isDeepDiscuss ? '\n見出し、箇条書き、太字、引用などのMarkdownを活用し、構造的で読みやすい形式で回答してください。' : '\n問いかけは1つだけに絞ってください。返答は1〜3文程度にまとめ、簡潔に回答してください。箇条書きは使わないでください。'}` ;
}

export function buildSummaryPrompt(messages, { memoHint, probQuotes = [] }) {
  const transcript = messages
    .map((message) => `${(message.source ?? message.role) === 'assistant' ? 'AI' : 'User'}: ${message.body ?? message.text}`)
    .join('\n\n');

  const probContext = probQuotes.length > 0
    ? `\nProbレポート引用情報:\n${probQuotes.map(q => `- レポート「${q.reportTitle}」の ${q.section} より: ${q.summary}`).join('\n')}\n\nサマリーのbody内で、上記のレポート内容に言及する場合は、必ず以下の形式の引用ブロックを含めてください（先頭は必ず「${PROB_QUOTE_PREFIX}」で始めること）：\n> ${PROB_QUOTE_PREFIX}${probQuotes[0].reportTitle}」の {section} より： {summary}\n`
    : '';

  return `以下のチャットログから、あとでメモ帳として読める「情報落ちを避けたサマリー」を作成し、指定のJSON形式で出力してください。

要件:
- ユーザーの発言を主な根拠として扱う。AIの提案や相槌は補助情報とし、ユーザー自身の意見やスタンスと混同しないこと。
- 三人称視点のメタ表現（例：「ユーザーは〜と言っています」「〜について話し合いました」）を排除する。
- ユーザー本人が書いたノートのように、直接的で再利用可能な言葉（「〜することにする」「〜は重要」「AよりBを優先」など）で記述する。
- 重要な固有名詞、数値、判断基準、迷い、未決事項、次アクションを具体的に残す。
- 短く圧縮しすぎず、会話内の情報を復元できる粒度で残す。
${probContext}
出力項目:
1. title: 短くスキャナブルな名詞句（例：「カフェの候補リスト」「Reactの設計方針」）。20文字〜30文字程度に抑え、「〜についての相談」や「AIの回答」などは避ける。
2. summary: ノートの内容を1文で端的に表したもの。
3. body: 構造化された詳細内容（Markdown形式）。見出し、箇条書き、表などを活用する。未決事項や次アクションもここに含まれるように構成すること。
4. tags: 内容に関連する短い日本語キーワード（タグ）の配列。最大10個。
   - 厳格なルール: 名詞ベースの純粋な「概念」や「キーワード」に限定すること。
   - 禁止事項: 指示文（例：「タグを抽出してください」「JSON形式で出力」）、文章、形容詞のみのタグ、重複。

余計なテキストは含めず、必ず以下の形式のJSONのみを出力してください。

{
  "title": "string (20-30 chars)",
  "summary": "string",
  "body": "string (markdown)",
  "tags": ["string", "string"] // Max 10 keywords
}

${memoHint ? `ユーザーからのNoteまとめヒント（これを優先してください）: ${memoHint}` : ''}

チャットログ:
${transcript}`;
}

export function buildInsightPrompt(note) {
  return `以下のNoteから、ユーザーのスタンス・暗黙知・矛盾・Missing Linkの候補を抽出してください。

重要:
- これは確定知識ではなく、ユーザー確認前のHint（思考の手がかり）です。
- ユーザー本人の発言や記録を主な根拠にしてください。
- AI応答由来の補助情報と、ユーザー本人の意見を混同しないでください。
- 「知識」「ToDo」「関心・問い」「判断基準」など、性質の異なる気づきが混在している場合は、それらを混ぜずに個別のHintオブジェクトとして分離して抽出してください。
- 行動項目は suggestedTargetType: "todo"、複数の行動を束ねる取り組みは suggestedTargetType: "project" として扱ってください。
- Noteが「明日のタスク」「買うもの」「一時的な作業メモ」のような短期・一時的な記録中心で、Hint化やAsset化までしない方が自然な場合は、無理に抽出せず insights: [] を返してください。
- 最大5件までに絞ってください。
- 必ず有効なJSONのみを返してください。Markdown、説明文、末尾カンマは不要です。
- typeは implicit_knowledge / conflict / missing_link / synthesis_suggestion のいずれか1つだけにしてください。
- suggestedTargetTypeは stance / framework / preference / knowledge / todo / project / interest のいずれか1つだけにしてください。（interest = 関心テーマや未解決の問い）
- titleは20文字〜30文字程度の短い名詞句にしてください。
- tagsは短い日本語キーワード（名詞）の配列にしてください（最大10個）。指示文や文章を含めないでください。
- 配列やオブジェクトの閉じ忘れに注意し、末尾のカンマは含めないでください。

JSON形式（この形式のみを出力してください）:
{
  "insights": [
    {
      "type": "implicit_knowledge",
      "suggestedTargetType": "knowledge",
      "title": "string (20-30 chars)",
      "summary": "string",
      "tags": ["keyword1", "keyword2"], // Max 10
      "confidence": 0.7
    }
  ]
}

Note title:
${note.title || ''}

Note summary:
${note.summary || ''}

Note body:
${note.body || ''}`;
}

export function buildTagInsightPrompt({ tag, notes, relatedInsights, relatedAssets }) {
  const noteText = notes
    .map((note, index) => `## Note ${index + 1}: ${note.title || 'Untitled'}\nSummary: ${note.summary || ''}\nBody:\n${note.body || ''}`)
    .join('\n\n');
  const insightText = relatedInsights
    .map((insight) => `- ${insight.title || 'Untitled Hint'}: ${insight.summary || ''}`)
    .join('\n');
  const assetText = relatedAssets
    .map((asset) => `- [${asset.type || 'knowledge'}] ${asset.title || 'Untitled Asset'}: ${asset.body || ''}`)
    .join('\n');

  return `タグ「${tag}」に紐づく複数Noteから、横断的なHint候補を抽出してください。

重要:
- これは確定知識ではなく、ユーザー確認前のHint（思考の手がかり）です。
- 単一Noteの要約ではなく、複数Noteの共通点、矛盾、Missing Link、Asset化できそうな種を抽出してください。
- 既存Hintや既存Assetと重複する候補は避け、差分がある場合だけ出してください。
- 「知識」「ToDo」「関心・問い」「判断基準」など、性質の異なる気づきが混在している場合は、個別のHintオブジェクトとして分離して抽出してください。
- 行動項目は suggestedTargetType: "todo"、複数の行動を束ねる取り組みは suggestedTargetType: "project" として扱ってください。
- 対象が短期ToDoや一時メモばかりで、横断的なHintにする価値が薄い場合は、無理に抽出せず insights: [] を返してください。
- 最大5件までに絞ってください。
- 必ず有効なJSONのみを返してください。Markdown、説明文、末尾カンマは不要です。
- typeは implicit_knowledge / conflict / missing_link / synthesis_suggestion のいずれか1つだけにしてください。
- suggestedTargetTypeは stance / framework / preference / knowledge / todo / project / interest のいずれか1つだけにしてください。（interest = 関心テーマや未解決の問い）
- titleは20文字〜30文字程度の短い名詞句にしてください。
- tagsは短い日本語キーワード（名詞）の配列にしてください（最大10個）。必ず「${tag}」を含め、指示文や文章は除外してください。
- 配列やオブジェクトの閉じ忘れに注意し、末尾のカンマは含めないでください。

JSON形式（この形式のみを出力してください）:
{
  "insights": [
    {
      "type": "synthesis_suggestion",
      "suggestedTargetType": "framework",
      "title": "string (20-30 chars)",
      "summary": "string",
      "tags": ["${tag}", "keyword2"], // Max 10
      "confidence": 0.7
    }
  ]
}

対象Note:
${noteText || 'なし'}

関連する既存Hint:
${insightText || 'なし'}

関連する既存Asset:
${assetText || 'なし'}`;
}

export function buildHealthCheckPrompt({ notes, concepts }) {
  const noteList = notes.map(n => ({
    id: n.id,
    title: n.title,
    tags: n.tags,
    summaryCompleteness: n.metadata?.summaryCompleteness,
    summary: n.summary,
    body: n.body?.slice(0, 500) // Send snippet for context
  }));

  const assetList = concepts.map(c => ({
    id: c.id,
    title: c.title,
    type: c.type,
    status: c.status,
    maturity: c.maturity,
    tags: c.tags,
    body: c.body?.slice(0, 500)
  }));

  return `あなたはSecond Brainのナレッジメンテナンス・エージェントです。蓄積されたNoteとAssetを監査し、更新や整理が必要なものを「提案（Proposals）」として抽出してください。

対象とするケース:
1. Note更新候補 (note_update)
   - metadata.summaryCompleteness: "partial" の Note (まとめが途切れている)
   - 要約が極端に短い、または内容が薄い Note (改善を提案)
2. Asset更新候補 (asset_update)
   - maturity: "seed" のまま長期間更新がない Asset
   - status: "active" だが、関連しそうな新しい情報を反映していない古い Asset

出力ルール:
- 既存のNote/Assetを直接書き換えるのではなく、修正案（patch）を提案してください。
- kind は必ず "proposed_change" としてください。
- type は "note_update" または "asset_update" としてください。
- targetId に対象ドキュメントのIDを指定してください。
- proposedPatch には、更新したいフィールドとその新しい値を含めてください（例: body, maturity, tags）。tagsを提案する場合は、タグ未設定だけを理由にせず、明確な整理効果がある場合に限り、最大10個の名詞ベースのキーワードに絞ってください。
- summary に、なぜその更新を提案するのかの理由を簡潔に記述してください。
- title には提案のタイトル（例: 「タグ追加の提案」「内容の充実化」）を20文字〜30文字程度で記述してください。
- 最大5件程度に絞ってください。
- 必ず有効なJSONのみを返してください。

JSON形式（この形式のみを出力してください）:
{
  "proposals": [
    {
      "kind": "proposed_change",
      "type": "note_update",
      "targetId": "string",
      "title": "string (20-30 chars)",
      "summary": "string",
      "proposedPatch": {
        "tags": ["keyword1", "keyword2"] // Max 10
      },
      "confidence": 0.9
    }
  ]
}

対象Note一覧:
${JSON.stringify(noteList, null, 2)}

対象Asset一覧:
${JSON.stringify(assetList, null, 2)}`;
}
